// BATCH OVERVIEW → topic/section analysis → student list (Q1, Q2, Q4, Q5,
// Q6, Q7, Q8, Q9 for one batch). Every section here reads an already-
// aggregated admin endpoint backed by the same per-student services the
// student dashboard calls — nothing is recalculated in this component.
import React, { useEffect, useState } from 'react';
import Icon from '../../components/ui/Icon';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import API from '../../services/api';
import PaginationControls from '../../components/ui/PaginationControls';
import { readinessLevelColor, readinessLevelLabel, scoreColor, withAlpha } from '../../components/diagnostic/diagnosticHelpers';
import { SkeletonCard } from '../../components/ui/Skeleton';
import '../admin/Analytics.css';
import './BatchDrilldown.css';

// Same 3-tier semantic scale the student experience uses (scoreColor from
// diagnosticHelpers) — this used to be a separately-hand-copied 4-tier scale
// with its own "on track" blue band at >=60%, meaning the identical 65%
// accuracy rendered as a neutral "on track" color here but amber/"needs
// work" on the student's own diagnostic view. Null-safety (gray for missing
// data, e.g. an unevaluated batch) is preserved locally since scoreColor
// itself treats a null score as 0 (red) rather than "no data".
const sc = v => v == null ? '#94a3b8' : scoreColor(v);
const fmt = (v, s = '') => (v == null ? '—' : `${v}${s}`);
const STATE_COLOR = {
  mastered: '#059669', strong: '#0891b2', improving: '#7c3aed',
  needs_attention: '#dc2626', declining: '#e11d48', insufficient_data: '#94a3b8',
};

// Empty state is rendered OUTSIDE the table/scroll-wrap entirely rather than
// as a wide colSpan cell inside it — a colSpan cell still inherits the
// table's full (wider-than-viewport) intrinsic width on narrow screens, so
// the message text was getting clipped by the table's own horizontal
// scroll instead of wrapping normally. A plain paragraph always reflows.
const ReadinessTable = ({ rows, emptyText, scoreLabel = 'Score' }) => (
  rows.length === 0 ? (
    <div className="axh-empty">{emptyText}</div>
  ) : (
  <div className="axh-table-wrap">
    <table className="axh-table">
      <thead><tr><th>Student</th><th>Roll No.</th><th>Branch</th><th>{scoreLabel}</th><th>Confidence</th></tr></thead>
      <tbody>
        {rows.map(r => (
          <tr key={r.student_id}>
            <td className="name-cell">{r.name || '—'}</td>
            <td>{r.roll_number || '—'}</td>
            <td>{r.branch || '—'}</td>
            <td style={{ fontWeight: 700, color: sc(r.score) }}>{r.score != null ? Number(r.score).toFixed(1) : '—'}</td>
            <td>{r.confidence || '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
  )
);
const BatchAnalytics = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [summary, setSummary] = useState(null);
  const [topics, setTopics] = useState(null);
  const [mistakes, setMistakes] = useState(null);
  const [trend, setTrend] = useState(null);
  const [students, setStudents] = useState(null);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [studentsPage, setStudentsPage] = useState(1);
  const [studentsSearch, setStudentsSearch] = useState('');
  const [studentsDebouncedSearch, setStudentsDebouncedSearch] = useState('');
  const [studentsBranch, setStudentsBranch] = useState('all');
  const [intervention, setIntervention] = useState(null);
  const [tab, setTab] = useState('overview');

  useEffect(() => {
    (async () => {
      setLoading(true); setError('');
      try {
        const [s, t, m, tr, iv] = await Promise.all([
          API.get(`/reports/batches/${id}/summary`),
          API.get(`/reports/batches/${id}/topics`),
          API.get(`/reports/batches/${id}/mistakes`),
          API.get(`/reports/batches/${id}/trend?weeks=26`),
          API.get(`/reports/batches/${id}/intervention`),
        ]);
        setSummary(s.data); setTopics(t.data); setMistakes(m.data);
        setTrend(tr.data); setIntervention(iv.data);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load batch analytics');
      } finally { setLoading(false); }
    })();
  }, [id]);

  // Students tab: server-side pagination (audit SA-08) — fetched only while this tab is open, since it's the one place
  // that runs the per-student readiness/topic/recommendation fan-out, and only for the page being viewed.
  useEffect(() => { const h = setTimeout(() => { setStudentsDebouncedSearch(studentsSearch.trim()); setStudentsPage(1); }, 300); return () => clearTimeout(h); }, [studentsSearch]);
  useEffect(() => { setStudentsPage(1); }, [studentsBranch]);
  useEffect(() => {
    if (tab !== 'students') return;
    let cancelled = false;
    setStudentsLoading(true);
    const params = { page: studentsPage, limit: 20 };
    if (studentsDebouncedSearch) params.search = studentsDebouncedSearch;
    if (studentsBranch !== 'all') params.branch = studentsBranch;
    API.get(`/reports/batches/${id}/students`, { params })
      .then(res => { if (!cancelled) setStudents(res.data); })
      .catch(err => { if (!cancelled) setError(err.response?.data?.message || 'Failed to load students'); })
      .finally(() => { if (!cancelled) setStudentsLoading(false); });
    return () => { cancelled = true; };
  }, [tab, id, studentsPage, studentsDebouncedSearch, studentsBranch]);

  if (loading) {
    return (
      <div className="axh-page">
        <div className="axh-kpi-grid">
          {Array.from({ length: 4 }).map((_, i) => <SkeletonCard key={i} lines={1} />)}
        </div>
        <SkeletonCard lines={5} />
      </div>
    );
  }
  if (error) return <div className="axh-page"><div className="axh-error">{error}</div></div>;

  const batchMeta = summary?.batch;
  const s = summary?.summary;
  const trendChartData = (trend?.buckets || []).map(b => ({
    week: new Date(b.week_start).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
    avg_score: b.avg_score,
  }));

  const TABS = [
    { key: 'overview', label: 'Readiness' },
    { key: 'topics', label: 'Topics & Sections' },
    { key: 'mistakes', label: 'Mistakes' },
    { key: 'trend', label: 'Trend' },
    { key: 'intervention', label: `Intervention${intervention?.students?.length ? ` (${intervention.students.length})` : ''}` },
    { key: 'students', label: 'Students' },
  ];

  return (
    <div className="axh-page">
      <div className="axh-topbar">
        <div>
          <Link to="/admin/batches-overview" className="bdx-breadcrumb"><Icon name="chevron-left" size={14} /> All Batches</Link>
          <h1>{batchMeta?.name}</h1>
          <p>{batchMeta?.student_count} students · same readiness/diagnostic intelligence students see on their own dashboard</p>
        </div>
      </div>

      <div className="axh-tabs" role="tablist" aria-label="Batch analytics sections">
        {TABS.map(t => (
          <button
            key={t.key}
            role="tab"
            aria-selected={tab === t.key}
            id={`batch-tab-${t.key}`}
            aria-controls={`batch-panel-${t.key}`}
            className={`axh-tab ${tab === t.key ? 'active' : ''}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && s && (
        <div role="tabpanel" id="batch-panel-overview" aria-labelledby="batch-tab-overview">
          <div className="axh-kpi-grid">
            <div className="axh-kpi"><div className="v">{s.evaluated}/{s.total_students}</div><div className="l">Evaluated</div><div className="sub">{s.insufficient_evidence} insufficient evidence</div></div>
            <div className="axh-kpi"><div className="v" style={{ color: sc(s.placement_ready_pct) }}>{fmt(s.placement_ready_pct, '%')}</div><div className="l">Placement Ready</div></div>
            <div className="axh-kpi"><div className="v" style={{ color: sc(s.avg_readiness_score) }}>{fmt(s.avg_readiness_score)}</div><div className="l">Avg Readiness Score</div></div>
            <div className="axh-kpi"><div className="v">{s.by_level.needs_improvement}</div><div className="l">Needs Improvement</div></div>
          </div>

          <section className="axh-section">
            <div className="axh-section-head"><h2>Level Breakdown</h2></div>
            <div className="axh-card">
              <div className="axh-bars">
                {Object.entries(s.by_level).map(([level, count]) => (
                  <div className="axh-bar-row" key={level}>
                    <span className="axh-bar-label">{readinessLevelLabel(level)}</span>
                    <div className="axh-bar-track"><div className="axh-bar-fill" style={{ width: `${s.total_students > 0 ? (count / s.total_students) * 100 : 0}%`, background: readinessLevelColor(level) }} /></div>
                    <span className="axh-bar-val">{count}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <div className="axh-grid-2">
            <section className="axh-section">
              <div className="axh-section-head"><h2>Needs Attention (Q2)</h2></div>
              <div className="axh-card"><ReadinessTable rows={summary.needs_attention} emptyText="No students currently flagged — either all evaluated students are ready, or evidence is still insufficient." /></div>
            </section>
            <section className="axh-section">
              <div className="axh-section-head"><h2>Placement Ready (Q1)</h2></div>
              <div className="axh-card"><ReadinessTable rows={summary.ready} emptyText="No students are currently placement-ready in this batch." /></div>
            </section>
          </div>

          <div className="axh-grid-2">
            <section className="axh-section">
              <div className="axh-section-head"><h2>Declining (Q6)</h2></div>
              <div className="axh-card"><ReadinessTable rows={summary.declining} emptyText="No declining trends detected." /></div>
            </section>
            <section className="axh-section">
              <div className="axh-section-head"><h2>Improving (Q5)</h2></div>
              <div className="axh-card"><ReadinessTable rows={summary.improving} emptyText="No improving trends detected yet." /></div>
            </section>
          </div>

          {summary.insufficient.length > 0 && (
            <section className="axh-section">
              <div className="axh-section-head"><h2>Insufficient Evidence</h2><p>These students haven't taken enough Aptitude/Diagnostic tests yet to be evaluated — not counted as "needing attention."</p></div>
              <div className="axh-card">
                <div className="axh-chips">
                  {summary.insufficient.map(r => <span className="axh-chip" key={r.student_id}>{r.name}</span>)}
                </div>
              </div>
            </section>
          )}
        </div>
      )}

      {tab === 'topics' && topics && (
        <div role="tabpanel" id="batch-panel-topics" aria-labelledby="batch-tab-topics">
          <section className="axh-section">
            <div className="axh-section-head"><h2>Batch Weak Areas (Q4)</h2><p>A topic only appears here when at least 3 students have real evidence AND ≥40% of them are needs_attention/declining — never from a single low-average score.</p></div>
            <div className="axh-card">
              {topics.weak_areas.length === 0 ? (
                <div className="axh-empty">No topic currently meets the batch weak-area threshold (enough evidenced students, genuinely weak share).</div>
              ) : (
                <div className="axh-table-wrap">
                  <table className="axh-table">
                    <thead><tr><th>Topic</th><th>Category</th><th>Students w/ Data</th><th>Weak Share</th><th>Avg Accuracy</th></tr></thead>
                    <tbody>
                      {topics.weak_areas.map(t => (
                        <tr key={t.topic_id}>
                          <td className="name-cell">{t.topic_name}</td>
                          <td>{t.category}</td>
                          <td>{t.students_with_data} / {t.students_seen}</td>
                          <td style={{ color: '#dc2626', fontWeight: 700 }}>{fmt(t.weak_share_pct, '%')}</td>
                          <td style={{ color: sc(t.avg_accuracy_pct) }}>{fmt(t.avg_accuracy_pct, '%')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </section>

          <section className="axh-section">
            <div className="axh-section-head"><h2>Section Averages</h2></div>
            <div className="axh-card">
              <div className="axh-bars">
                {topics.sections.length === 0 ? <div className="axh-empty">No section-level evidence yet.</div> : topics.sections.map(sec => (
                  <div className="axh-bar-row" key={sec.category}>
                    <span className="axh-bar-label">{sec.category}</span>
                    <div className="axh-bar-track"><div className="axh-bar-fill" style={{ width: `${sec.avg_accuracy_pct || 0}%`, background: sc(sec.avg_accuracy_pct) }} /></div>
                    <span className="axh-bar-val" style={{ color: sc(sec.avg_accuracy_pct) }}>{fmt(sec.avg_accuracy_pct, '%')}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="axh-section">
            <div className="axh-section-head"><h2>All Topics</h2></div>
            <div className="axh-card">
              <div className="axh-table-wrap">
                <table className="axh-table">
                  <thead><tr><th>Topic</th><th>Category</th><th>Mastered</th><th>Strong</th><th>Improving</th><th>Needs Attention</th><th>Declining</th><th>Insufficient Data</th></tr></thead>
                  <tbody>
                    {topics.topics.map(t => (
                      <tr key={t.topic_id}>
                        <td className="name-cell">{t.topic_name}</td>
                        <td>{t.category}</td>
                        {['mastered', 'strong', 'improving', 'needs_attention', 'declining', 'insufficient_data'].map(st => (
                          <td key={st} style={{ color: t.state_counts[st] > 0 ? STATE_COLOR[st] : '#e2e8f0', fontWeight: t.state_counts[st] > 0 ? 700 : 400 }}>{t.state_counts[st]}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        </div>
      )}

      {tab === 'mistakes' && mistakes && (
        <div role="tabpanel" id="batch-panel-mistakes" aria-labelledby="batch-tab-mistakes">
          <div className="axh-kpi-grid">
            <div className="axh-kpi"><div className="v">{mistakes.total_unresolved}</div><div className="l">Unresolved</div></div>
            <div className="axh-kpi"><div className="v" style={{ color: '#dc2626' }}>{mistakes.total_recurring}</div><div className="l">Recurring</div></div>
            <div className="axh-kpi"><div className="v" style={{ color: '#059669' }}>{mistakes.total_recently_corrected}</div><div className="l">Recently Corrected</div></div>
          </div>
          <section className="axh-section">
            <div className="axh-section-head"><h2>Where Mistakes Concentrate (Q7)</h2>{mistakes.note && <p>{mistakes.note}</p>}</div>
            <div className="axh-card">
              {mistakes.topic_concentration.length === 0 ? <div className="axh-empty">No recurring mistakes recorded in this batch yet.</div> : (
                <div className="axh-table-wrap">
                  <table className="axh-table">
                    <thead><tr><th>Topic</th><th>Students Affected</th><th>Total Mistake Count</th></tr></thead>
                    <tbody>
                      {mistakes.topic_concentration.map(t => (
                        <tr key={t.topic_name}><td className="name-cell">{t.topic_name}</td><td>{t.students_affected}</td><td>{t.total_mistake_count}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </section>
          <section className="axh-section">
            <div className="axh-section-head"><h2>Students With Recurring Mistakes</h2></div>
            <div className="axh-card">
              {mistakes.students_with_recurring_mistakes.length === 0 ? <div className="axh-empty">None.</div> : (
                <div className="axh-chips">
                  {mistakes.students_with_recurring_mistakes.map(s => (
                    <span className="axh-chip" key={s.student_id}>{s.name} · {s.recurring_count} recurring</span>
                  ))}
                </div>
              )}
            </div>
          </section>
        </div>
      )}

      {tab === 'trend' && (
        <section className="axh-section" role="tabpanel" id="batch-panel-trend" aria-labelledby="batch-tab-trend">
          <div className="axh-section-head"><h2>Readiness Over Time (Q9)</h2><p>Weekly average of the same ERI-based score readinessEngine reports — naive percentage-only submissions are excluded.</p></div>
          <div className="axh-card">
            {trendChartData.length === 0 ? (
              <div className="axh-empty">Not enough historical readiness evidence yet to show a trend.</div>
            ) : (
              <div role="img" aria-label={`Weekly average readiness score chart across ${trendChartData.length} week${trendChartData.length === 1 ? '' : 's'}, ranging from ${Math.min(...trendChartData.map(d => d.avg_score ?? 0))}% to ${Math.max(...trendChartData.map(d => d.avg_score ?? 0))}%`}>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={trendChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eeecf9" />
                    <XAxis dataKey="week" tick={{ fontSize: 11 }} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="avg_score" fill="#6d28d9" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </section>
      )}

      {tab === 'intervention' && intervention && (
        <section className="axh-section" role="tabpanel" id="batch-panel-intervention" aria-labelledby="batch-tab-intervention">
          <div className="axh-section-head"><h2>Requires Intervention (Q8)</h2><p>Flagged from real signals only — a needs-improvement readiness level, a declining topic, or a high-priority reinforcement/mistake-review recommendation.</p></div>
          <div className="axh-card">
            {intervention.students.length === 0 ? (
              <div className="axh-empty">No student currently meets the intervention criteria.</div>
            ) : (
              <div className="bdx-intervention-list">
                {intervention.students.map(st => (
                  <div
                    className="bdx-intervention-row"
                    key={st.student_id}
                    role="button"
                    tabIndex={0}
                    aria-label={`View intelligence for ${st.name}`}
                    onClick={() => navigate(`/admin/students/${st.student_id}/intelligence?from=${id}`)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/admin/students/${st.student_id}/intelligence?from=${id}`); } }}
                  >
                    <div className="bdx-intervention-top">
                      <span className="name-cell">{st.name}</span>
                      <span className="axh-badge" style={{ background: 'var(--z-danger-tint)', color: 'var(--z-danger)' }}>severity {st.severity_score != null ? st.severity_score.toFixed(2) : '—'}</span>
                    </div>
                    <ul>{st.reasons.map((r, i) => <li key={i}>{r}</li>)}</ul>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {tab === 'students' && (
        <section className="axh-section" role="tabpanel" id="batch-panel-students" aria-labelledby="batch-tab-students">
          <div className="axh-section-head"><h2>Students</h2><p>Click a row for the full diagnostic/recommendation drill-down.</p></div>
          <div className="axh-card">
            <div style={{ display: 'flex', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
              <input type="text" placeholder="Search name or roll no…" value={studentsSearch} onChange={e => setStudentsSearch(e.target.value)} className="axh-filter-select" style={{ minWidth: 200 }} />
              {students?.filters?.branches?.length > 0 && (
                <select className="axh-filter-select" value={studentsBranch} onChange={e => setStudentsBranch(e.target.value)}>
                  <option value="all">All Branches</option>
                  {students.filters.branches.map(b => <option key={b} value={b}>{b}</option>)}
                </select>
              )}
            </div>
            {studentsLoading ? <div className="axh-empty">Loading…</div> : !students || students.data.length === 0 ? <div className="axh-empty">No students match</div> : <>
            <div className="axh-table-wrap">
              <table className="axh-table">
                <thead><tr><th>Student</th><th>Roll No.</th><th>Branch</th><th>Readiness</th><th>Score</th><th>Trend</th><th>Topics Needing Attention</th><th>Intervention</th></tr></thead>
                <tbody>
                  {students.data.map(s2 => (
                    <tr
                      key={s2.student_id}
                      role="button"
                      tabIndex={0}
                      aria-label={`View intelligence for ${s2.name}`}
                      onClick={() => navigate(`/admin/students/${s2.student_id}/intelligence?from=${id}`)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/admin/students/${s2.student_id}/intelligence?from=${id}`); } }}
                    >
                      <td className="name-cell">{s2.name}</td>
                      <td>{s2.roll_number}</td>
                      <td>{s2.branch}</td>
                      <td>{s2.readiness_available ? <span className="axh-pill" style={{ background: withAlpha(readinessLevelColor(s2.readiness_level), 13), color: readinessLevelColor(s2.readiness_level) }}>{readinessLevelLabel(s2.readiness_level)}</span> : <span className="axh-pill" style={{ background: 'var(--z-bg-alt)', color: 'var(--z-muted-2)' }}>Insufficient Data</span>}</td>
                      <td style={{ fontWeight: 700, color: sc(s2.readiness_score) }}>{s2.readiness_score != null ? Number(s2.readiness_score).toFixed(1) : '—'}</td>
                      <td>{s2.readiness_trend !== 'insufficient_data' ? s2.readiness_trend : '—'}</td>
                      <td>{s2.topics_needing_attention}</td>
                      <td>{s2.requires_intervention ? <span className="axh-badge" style={{ background: 'var(--z-danger-tint)', color: 'var(--z-danger)' }}>Yes</span> : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <PaginationControls page={studentsPage} totalPages={students.metadata.total_pages} total={students.metadata.total} onChange={setStudentsPage} />
            </>}
          </div>
        </section>
      )}
    </div>
  );
};

export default BatchAnalytics;
